import { useEffect, useMemo, useState, type SyntheticEvent } from 'react';

import { isParticlePapEligible } from '../features/ttqib/papDisplay';
import { useKnowledgeParticleOverlays } from '../hooks/useKnowledgeParticleOverlays';
import { useKnowledgeParticlePap } from '../hooks/useKnowledgeParticlePap';
import type { KnowledgeParticle } from '../types/domain';
import { safeExtractText } from '../utils/textHelper';
import {
  overlayLayerIsExtracting,
  resolveParticleCoachOverlayDisplay,
  resolveParticleLearnerOverlayDisplay,
  type OverlayLayerKind,
} from '../utils/particleOverlayDisplay';
import {
  GRAPH_PARTICLE_TICKET_PERFORATION_WIDTH,
  GraphParticleTicketPerforation,
  RELATED_GRAPH_OVERLAY_STRIP_WIDTH,
  RELATED_GRAPH_PAP_STRIP_EXTENSION_WIDTH,
} from './GraphParticleTicketChrome';
import { KnowledgeParticleOverlayDetailModal, KnowledgeParticleOverlayStubPanel } from './KnowledgeParticleOverlayDetailModal';
import { KnowledgeParticlePapDetailModal, KnowledgeParticlePapStubPanel } from './KnowledgeParticlePapDetailModal';

function mergeOverlayMetadata(
  particleMetadata: Record<string, unknown> | null | undefined,
  localOverlayMeta: Record<string, unknown> | null | undefined,
): Record<string, unknown> {
  return { ...(particleMetadata || {}), ...(localOverlayMeta || {}) };
}

function overlayMetaError(
  metadata: Record<string, unknown> | null | undefined,
  layer: OverlayLayerKind,
): string | null {
  if (!metadata) return null;
  const raw = layer === 'coach' ? metadata.coachOverlayError : metadata.learnerEvidenceError;
  const text = String(raw || '').trim();
  return text || null;
}

function stopNodeEvent(event: SyntheticEvent) {
  event.stopPropagation();
}

type GraphNodeParticlePapStripProps = {
  particleId: string;
  subject?: string | null;
  selected?: boolean;
  particleTitle?: string | null;
  questionSource?: unknown;
  answerSource?: unknown;
  particle?: KnowledgeParticle | null;
  particleMetadata?: Record<string, unknown> | null;
  onParticleUpdated?: (particle: KnowledgeParticle) => void;
  onStubInteract?: () => void;
};

export function GraphNodeParticlePapStrip({
  particleId,
  subject,
  selected,
  particleTitle,
  questionSource,
  answerSource,
  particle = null,
  particleMetadata = null,
  onParticleUpdated,
  onStubInteract,
}: GraphNodeParticlePapStripProps) {
  const [papDetailVisible, setPapDetailVisible] = useState(false);
  const [overlayLayer, setOverlayLayer] = useState<OverlayLayerKind | null>(null);
  const [localOverlayMeta, setLocalOverlayMeta] = useState<Record<string, unknown> | null>(null);
  const eligible = isParticlePapEligible({
    subject,
    questions: questionSource,
    answers: answerSource,
  });
  const overlayMeta = useMemo(
    () => mergeOverlayMetadata(particleMetadata, localOverlayMeta),
    [particleMetadata, localOverlayMeta],
  );
  const { papView, loading, rebuilding, error, rebuildPap } = useKnowledgeParticlePap({
    particleId,
    subject,
    questions: questionSource,
    answers: answerSource,
    enabled: eligible,
  });
  const { extracting: overlayExtracting, error: overlayExtractError, extractOverlays } = useKnowledgeParticleOverlays({
    particleId,
    enabled: eligible,
    autoKickoff: eligible,
    particle: particle || ({
      id: particleId,
      metadata: overlayMeta,
    } as KnowledgeParticle),
    onParticleUpdated: (next) => {
      const meta = (next.metadata || {}) as Record<string, unknown>;
      setLocalOverlayMeta((prev) => ({
        ...(prev || {}),
        coachOverlay: meta.coachOverlay,
        coachOverlayStatus: meta.coachOverlayStatus,
        coachOverlayError: meta.coachOverlayError,
        learnerEvidence: meta.learnerEvidence,
        learnerEvidenceStatus: meta.learnerEvidenceStatus,
        learnerEvidenceError: meta.learnerEvidenceError,
      }));
      onParticleUpdated?.(next);
    },
  });

  const coachDisplay = useMemo(
    () => resolveParticleCoachOverlayDisplay(overlayMeta),
    [overlayMeta],
  );
  const learnerDisplay = useMemo(
    () => resolveParticleLearnerOverlayDisplay(overlayMeta),
    [overlayMeta],
  );
  const coachExtracting = overlayLayerIsExtracting({
    layer: 'coach',
    metadata: overlayMeta,
    extracting: overlayExtracting,
    hasContent: Boolean(coachDisplay?.hasContent),
  });
  const learnerExtracting = overlayLayerIsExtracting({
    layer: 'learner',
    metadata: overlayMeta,
    extracting: overlayExtracting,
    hasContent: Boolean(learnerDisplay?.hasContent),
  });
  const coachExtractError = overlayMetaError(overlayMeta, 'coach') || overlayExtractError;
  const learnerExtractError = overlayMetaError(overlayMeta, 'learner') || overlayExtractError;
  const papCouponWidth = RELATED_GRAPH_PAP_STRIP_EXTENSION_WIDTH - GRAPH_PARTICLE_TICKET_PERFORATION_WIDTH;

  useEffect(() => {
    if (!overlayLayer) return;
    const hasContent = overlayLayer === 'coach'
      ? Boolean(coachDisplay?.hasContent)
      : Boolean(learnerDisplay?.hasContent);
    const status = String(
      overlayLayer === 'coach'
        ? overlayMeta.coachOverlayStatus
        : overlayMeta.learnerEvidenceStatus,
    ).trim().toLowerCase();
    if (hasContent || overlayExtracting || status === 'running' || status === 'succeeded') {
      return;
    }
    void extractOverlays(overlayLayer, { force: false, wait: false });
  }, [overlayLayer]);

  if (!eligible) return null;

  return (
    <>
      <div className="flex min-w-0 flex-1 self-stretch">
        <GraphParticleTicketPerforation selected={selected} />
        <button
          type="button"
          data-graph-stub="pap"
          className="nodrag nopan nowheel flex self-stretch bg-[#F4F8FF] text-left hover:bg-[#EAF2FF]"
          style={{ width: papCouponWidth }}
          aria-label="查看隐性能力详情"
          onPointerDown={(event) => {
            onStubInteract?.();
            stopNodeEvent(event);
          }}
          onClick={(event) => {
            stopNodeEvent(event);
            setOverlayLayer(null);
            setPapDetailVisible(true);
          }}
        >
          <KnowledgeParticlePapStubPanel
            papView={papView}
            loading={loading}
            error={error}
          />
        </button>
        <button
          type="button"
          data-graph-stub="coach"
          className="nodrag nopan nowheel flex self-stretch border-l border-black/10 bg-[rgba(180,83,9,0.06)] text-left hover:bg-[rgba(180,83,9,0.10)]"
          style={{ width: RELATED_GRAPH_OVERLAY_STRIP_WIDTH }}
          aria-label="查看教练层"
          onPointerDown={(event) => {
            onStubInteract?.();
            stopNodeEvent(event);
          }}
          onClick={(event) => {
            stopNodeEvent(event);
            setPapDetailVisible(false);
            setOverlayLayer('coach');
          }}
        >
          <KnowledgeParticleOverlayStubPanel
            layer="coach"
            coachDisplay={coachDisplay}
            extracting={coachExtracting}
            extractError={coachExtracting ? null : coachExtractError}
          />
        </button>
        <button
          type="button"
          data-graph-stub="learner"
          className="nodrag nopan nowheel flex self-stretch border-l border-black/10 bg-[rgba(13,148,136,0.06)] text-left hover:bg-[rgba(13,148,136,0.10)]"
          style={{ width: RELATED_GRAPH_OVERLAY_STRIP_WIDTH }}
          aria-label="查看学习层"
          onPointerDown={(event) => {
            onStubInteract?.();
            stopNodeEvent(event);
          }}
          onClick={(event) => {
            stopNodeEvent(event);
            setPapDetailVisible(false);
            setOverlayLayer('learner');
          }}
        >
          <KnowledgeParticleOverlayStubPanel
            layer="learner"
            learnerDisplay={learnerDisplay}
            extracting={learnerExtracting}
            extractError={learnerExtracting ? null : learnerExtractError}
          />
        </button>
      </div>

      <KnowledgeParticlePapDetailModal
        visible={papDetailVisible}
        onClose={() => setPapDetailVisible(false)}
        papView={papView}
        loading={loading}
        error={error}
        rebuilding={rebuilding}
        onRebuild={() => { void rebuildPap(); }}
        particleTitle={particleTitle || undefined}
        questionSnippet={safeExtractText(questionSource) || undefined}
      />

      <KnowledgeParticleOverlayDetailModal
        visible={overlayLayer != null}
        onClose={() => setOverlayLayer(null)}
        layer={overlayLayer || 'coach'}
        coachDisplay={coachDisplay}
        learnerDisplay={learnerDisplay}
        extracting={overlayLayer === 'learner' ? learnerExtracting : coachExtracting}
        extractError={overlayLayer === 'learner' ? learnerExtractError : coachExtractError}
        onExtract={() => {
          void (async () => {
            const layer = overlayLayer === 'learner' ? 'learner' : 'coach';
            const view = await extractOverlays(layer, { force: true, wait: false });
            if (!view) return;
            setLocalOverlayMeta((prev) => ({
              ...(prev || {}),
              ...(layer === 'coach'
                ? {
                  coachOverlay: view.coachOverlay,
                  coachOverlayStatus: view.coachOverlayStatus,
                  coachOverlayError: view.coachOverlayError,
                }
                : {
                  learnerEvidence: view.learnerEvidence,
                  learnerEvidenceStatus: view.learnerEvidenceStatus,
                  learnerEvidenceError: view.learnerEvidenceError,
                }),
            }));
          })();
        }}
      />
    </>
  );
}
